import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Target, CornerDownLeft } from 'lucide-react';
import { Input } from '../ui/Input.jsx';
import { haptic } from '../../utils/haptics';

const MAX_LENGTH = 200;

/**
 * Ein-Zeilen-Composer für neue Lernziele: tippen, Enter, fertig.
 *
 * Bewusst ohne Zieltyp, Kategorie, Priorität und Details — angelegt wird immer ein
 * freies Ziel. Session-Ziele entstehen dort, wo sie hingehören: beim Anlegen einer
 * Session und im Session-Feedback.
 */
const GoalComposer = ({ onCreate }) => {
  const [value, setValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const submit = async () => {
    const text = value.trim();
    if (!text || isSaving) return;

    // Senden = leichter Impuls; ein Fehler vibriert über toast.error des Aufrufers
    haptic?.light?.();
    setIsSaving(true);
    // Feld sofort leeren, damit direkt das nächste Ziel getippt werden kann
    setValue('');
    try {
      await onCreate(text);
    } catch (error) {
      // Text zurückgeben, damit die Eingabe nicht verloren geht
      setValue((current) => (current ? current : text));
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      setValue('');
    }
  };

  return (
    <div className="relative">
      {/* Zielscheibe statt Plus (Okt 2026, wie im Spot): Das Feld fragt nach
          einem Ziel, es legt nicht „irgendetwas“ an. */}
      <Target
        className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
        aria-hidden="true"
      />
      <Input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        maxLength={MAX_LENGTH}
        disabled={isSaving}
        placeholder="Was willst du lernen?"
        aria-label="Neues Lernziel"
        className="pl-10 pr-14"
      />
      {/* Absenden per Tastatur reicht am Desktop; auf Mobile braucht es einen sichtbaren Weg */}
      {value.trim() && (
        <button
          type="button"
          onClick={submit}
          disabled={isSaving}
          aria-label="Lernziel hinzufügen"
          className="absolute right-1.5 top-1/2 flex min-h-[44px] min-w-[44px] -translate-y-1/2 items-center justify-center rounded-xl text-primary transition-colors duration-base hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
        >
          <CornerDownLeft className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

GoalComposer.propTypes = {
  onCreate: PropTypes.func.isRequired
};

export default GoalComposer;
